import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { History, Download, CheckCircle2, Ban, Info, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { downloadJSON } from "../../lib/api";
import { Panel, Btn, Overline } from "../ui";
import { useI18n } from "../../lib/i18n";

const META = {
  success: { color: "text-aegis-emerald", ring: "border-aegis-emerald/50", icon: CheckCircle2 },
  blocked: { color: "text-aegis-red", ring: "border-aegis-red/50", icon: Ban },
  warning: { color: "text-aegis-amber", ring: "border-aegis-amber/50", icon: AlertTriangle },
  standard: { color: "text-aegis-cyan", ring: "border-aegis-cyan/50", icon: Info },
};

const FILTERS = ["all", "success", "blocked", "standard", "warning"];

export default function AuditTimeline({ logs }) {
  const { t } = useI18n();
  const [filter, setFilter] = useState("all");

  const rows = useMemo(() => {
    const ordered = [...(logs || [])].reverse();
    return filter === "all" ? ordered : ordered.filter((l) => (l.level || "standard") === filter);
  }, [logs, filter]);

  return (
    <div className="space-y-6" data-testid="screen-audit">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Overline>{t("audit.overline")}</Overline>
          <h1 className="mt-1.5 font-heading text-3xl font-light tracking-tighter text-slate-100">{t("audit.title")}</h1>
          <p className="mt-2 text-sm text-slate-400">{t("audit.sub")}</p>
        </div>
        <Btn
          variant="ghost"
          testid="audit-export-btn"
          onClick={() => {
            import("../../lib/time").then(({ nowIsoUtc }) => {
              downloadJSON(`veralix-audit-${Date.now()}.json`, { exported_at: nowIsoUtc(), events: logs });
              toast.success("Timeline exported");
            });
          }}
        >
          <Download size={15} /> {t("audit.export")}
        </Btn>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`audit-filter-${f}`}
            className={`rounded-full border px-3.5 py-1.5 text-[12px] transition-colors duration-200 ${
              filter === f
                ? "border-aegis-cyan/40 bg-aegis-cyan/10 text-aegis-cyan"
                : "border-edge raise text-slate-400 hover:text-slate-200"
            }`}
          >
            {t(`audit.${f}`)}
          </button>
        ))}
        <span className="ml-auto self-center font-mono text-[11px] text-slate-400" data-testid="audit-count">
          {t("audit.events", { n: rows.length })}
        </span>
      </div>

      <Panel icon={History} title={t("audit.title")} subtitle={t("audit.events", { n: rows.length })} accent="cyan" testid="audit-panel">
        {rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">{t("audit.empty")}</p>
        ) : (
          <div className="relative pl-7">
            <span className="absolute left-[11px] top-2 bottom-2 w-px bg-gradient-to-b from-aegis-cyan/40 via-edge to-transparent" />
            <div className="space-y-3">
              {rows.map((l, i) => {
                const m = META[l.level] || META.standard;
                const Icon = m.icon;
                return (
                  <motion.div
                    key={`${l.timestamp}-${i}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.025, 0.4) }}
                    className="relative"
                    data-testid={`audit-row-${i}`}
                  >
                    <span
                      className={`absolute -left-[26px] top-3 grid h-[22px] w-[22px] place-items-center rounded-full border bg-void ${m.ring} ${m.color}`}
                    >
                      <Icon size={11} />
                    </span>
                    <div className="rounded-lg border border-edge raise px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`font-mono text-[10px] uppercase tracking-overline ${m.color}`}>{l.level || "standard"}</span>
                        <span className="font-mono text-[10px] text-slate-400">
                          {new Date(l.timestamp).toLocaleString("en-GB", { hour12: false })}
                        </span>
                      </div>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-slate-300">{l.message}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
